// Stays the first import: modules below call `i18next.t` at module scope (see ./i18n).
import './i18n';

import mainTheme from '@kimuno/theme';
import { ThemeProvider } from '@mui/material';
import config from 'config';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { ampli } from './ampli';
import ToastList from './components/ToastList';
import { mapNodeEnvToAmpliEnv } from './constants';
import Router from './router/router';
import BannerContextProvider from './state/BannerContextProvider';
import store from './store';
import { setAmpliReady } from './utils/ampli';
import bugsnagClient, { notifyBugsnagHandledError } from './utils/bugsnagClient';
import { isFeatureEnabled } from './utils/feature';
import { loadGa4BrowserTag } from './utils/ga4BrowserTag';
import { installHistorySafetyGuard } from './utils/historySafety';
import { loadCookiebot, loadPaymentLibraries, loadPSPDFkit } from './utils/loadScript';
import { installMatchMediaEventTargetPolyfill } from './utils/matchMediaEventTarget';

const ErrorBoundary = bugsnagClient.getPlugin('react').createErrorBoundary(React);

async function loadPolyfills() {
	// Synchronous and first: MUI reads `window.matchMedia` on every `useMediaQuery` call, so the
	// wrap has to be in place before anything renders.
	installMatchMediaEventTargetPolyfill();

	await import('@ungap/global-this');

	if (typeof window.IntersectionObserver === 'undefined') {
		await import('intersection-observer');
	}

	if (typeof window.ResizeObserver === 'undefined') {
		import('resize-observer-polyfill').then((ResizeObserver) => {
			window.ResizeObserver = ResizeObserver.default;
		});
	}
}

const initializeAmpli = () => {
	if (process.env.NODE_ENV && process.env.NODE_ENV in mapNodeEnvToAmpliEnv) {
		const neuDomain = config.urls.neu.replace(/(^\w+:|^)\/\//, '');
		// `urls.neu` only names the canonical host of the stage's config, so a host
		// that config doesn't know about — a PR preview, or any future host — would
		// see its own navigations counted as external referrers. Exclude the host
		// actually being served alongside it.
		const selfDomains = [...new Set([neuDomain, window.location.hostname])];
		const excludeDomainRegexes = selfDomains.map(
			(domain) => new RegExp(`${domain.replace(/\./g, '\\.')}$`)
		);

		ampli
			.load({
				environment: mapNodeEnvToAmpliEnv[process.env.NODE_ENV],
				client: {
					configuration: {
						defaultTracking: {
							attribution: {
								excludeReferrers: excludeDomainRegexes,
							},
							pageViews: false,
							sessions: false,
							fileDownloads: false,
							formInteractions: false,
						},
						trackingOptions: {
							ipAddress: false,
						},
					},
				},
			})
			.promise.then(() => {
				setAmpliReady();
			})
			.catch((error) => {
				notifyBugsnagHandledError(error);
				// Unblock waiters so tracking calls don't hang forever
				setAmpliReady();
			});
	} else {
		notifyBugsnagHandledError(Error(`NODE_ENV is invalid! NODE_ENV: ${process.env.NODE_ENV}`));
	}
};

function loadScripts() {
	// Load essential scripts
	loadPSPDFkit();

	if (isFeatureEnabled('ampli')) initializeAmpli();
	if (isFeatureEnabled('cookieBot')) loadCookiebot();
	if (isFeatureEnabled('payment')) loadPaymentLibraries();

	// Staging only, and a no-op everywhere else (SUB-329). Waits on consent, so it
	// must not block the rest of the boot.
	void loadGa4BrowserTag().catch(notifyBugsnagHandledError);
}

const main = async () => {
	const appContainer = document.getElementById('app');
	if (!appContainer) return;

	installHistorySafetyGuard();
	loadScripts();

	createRoot(appContainer, {
		// The production build drops `console.*`, so React's default report of an error it
		// recovered from by re-rendering would be invisible.
		onRecoverableError: (error, errorInfo) =>
			notifyBugsnagHandledError(error instanceof Error ? error : new Error(String(error)), {
				react: { componentStack: errorInfo.componentStack },
			}),
	}).render(
		<ErrorBoundary>
			<Provider store={store}>
				<ThemeProvider theme={mainTheme}>
					<ToastList />
					<BannerContextProvider>
						<Router />
					</BannerContextProvider>
				</ThemeProvider>
			</Provider>
		</ErrorBoundary>
	);
};

loadPolyfills().then(main);
